import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useFormik } from 'formik';

import { useAccess } from '../../../hooks/useAccess';
import { useLanguage } from '../../../context/LanguageContext';
import { useLayout } from '../../../context/LayoutContext';
import { useNotFound } from '../../../hooks/useNotFound';
import {
  useGetExerciseLoadFormulas,
  useGetFilePaths,
} from '../../../services/redux/api/appConfigApi';
import {
  useGetExerciseOptions,
  useGetExerciseProperties,
} from '../../../services/redux/api/exerciseConfigAPI';
import {
  useGenerateExerciseDescriptionMutation,
  useTranslateMissingMutation,
} from '../../../services/redux/api/aiAdminAPI';
import {
  useLazyGetExerciseById,
  usePostPutExercise,
} from '../../../services/redux/api/exercisesAPI';
import {
  useGetLanguages,
  useGetTranslations,
} from '../../../services/redux/api/translationsAPI';
import { ExercisePropertyType, ExerciseScope } from '../../../shared/constants';
import { IAppFile } from '../../../types/interfaces/app-file';
import { INewFile } from '../../../types/interfaces/new-file';
import { orderBy } from '../../../utils/array-utils';
import {
  deleteFilesFromFirebase,
  uploadFileAndCreateAppFile,
} from '../../../utils/firebase-storage-utils';
import { getExerciseValidation } from './FormikData';
import { ExerciseTranslation } from '../../../types/interfaces/exercise-translation';
import type { IExerciseProperty } from '../../../types/interfaces/exercise-property';
import type { TranslateItemRequest } from '../../../services/redux/api/aiAdminAPI';

interface UseNewExercisePageOptions {
  onSuccess?: () => void;
}

function isGifMimeType(mimeType?: string): boolean {
  return String(mimeType || '').toLowerCase() === 'image/gif';
}

function findDefaultExerciseProperty(
  list: IExerciseProperty[] | undefined,
): IExerciseProperty | null {
  return list?.find((p) => p.isDefault) ?? null;
}

export function useNewExercisePage(
  exerciseId?: string,
  options?: UseNewExercisePageOptions,
) {
  const isNewExercise = !exerciseId;
  const [postPutPostExercise, { isLoading }] = usePostPutExercise();
  const [generateDescription, { isLoading: isGenerateDescriptionLoading }] =
    useGenerateExerciseDescriptionMutation();
  const [translateMissing, { isLoading: isTranslateMissingLoading }] =
    useTranslateMissingMutation();
  const navigate = useNavigate();
  const { data: exerciseOptions } = useGetExerciseOptions();
  const [
    getExercise,
    { data: exercise, error, isFetching: isExerciseFetching },
  ] = useLazyGetExerciseById();
  const { currentLanguage } = useLanguage();
  const { t } = useTranslation();
  const { access } = useAccess();
  const { showLoader, hideLoader } = useLayout();
  const exerciseLoaderIdRef = useRef<number | null>(null);
  const aiUse = access?.aiUse;
  const [selectedFile, setSelectedFile] = useState(null);
  const { data: translations } = useGetTranslations();
  const { data: languagesResponse } = useGetLanguages();

  const languages = useMemo(
    () => orderBy(languagesResponse, 'title') || [],
    [languagesResponse],
  );

  const { data: properties } = useGetExerciseProperties();
  const { data: formulas } = useGetExerciseLoadFormulas();
  const { data: filePaths } = useGetFilePaths();
  const [fileIdsToDelete, setFileIdsToDelete] = useState<string[]>([]);
  const [filesToUpload, setFilesToUpload] = useState<INewFile[]>([]);

  const categories = orderBy(
    properties?.filter((p) => p.type === ExercisePropertyType.category),
    'priority',
  );
  const mainMuscleGroups = orderBy(
    properties?.filter((p) => p.type === ExercisePropertyType.muscle),
    'priority',
  );
  const equipments = orderBy(
    properties?.filter((p) => p.type === ExercisePropertyType.equipment),
    'priority',
  );
  const developedSkills = orderBy(
    properties?.filter((p) => p.type === ExercisePropertyType.developedSkill),
    'priority',
  );
  const exerciseScope = [
    {
      id: ExerciseScope.private,
      name: t('exercises.scope_private'),
    },
    {
      id: ExerciseScope.public,
      name: t('exercises.scope_public'),
    },
  ];

  const isExerciseLoading = !!exerciseId && (!exercise || isExerciseFetching);
  const validationSchema = useMemo(
    () =>
      getExerciseValidation(t, {
        skipTranslationsCheck: isExerciseLoading,
      }),
    [t, isExerciseLoading],
  );

  const formik = useFormik({
    enableReinitialize: true,
    validateOnMount: false,
    validateOnChange: true,
    validateOnBlur: true,
    validationSchema,
    initialValues: {
      scope: exercise?.scope
        ? exerciseScope.find((s) => s.id === exercise?.scope)
        : exerciseScope[0],
      category: isNewExercise
        ? findDefaultExerciseProperty(categories)
        : exercise != null
          ? (exercise.category ?? null)
          : null,
      mainMuscleGroup: isNewExercise
        ? findDefaultExerciseProperty(mainMuscleGroups)
        : exercise != null
          ? (exercise.mainMuscleGroup ?? null)
          : null,
      equipment: isNewExercise
        ? findDefaultExerciseProperty(equipments)
        : exercise != null
          ? (exercise.equipment ?? null)
          : null,
      additionalEquipments: exercise?.additionalEquipments || [],
      additionalMuscles: exercise?.additionalMuscles || [],
      developedSkills: exercise?.developedSkills || [],
      avatarFileId: exercise?.avatarFileId ?? exercise?.avatar?.id ?? null,
      splitBySide: exercise?.splitBySide || false,
      language: languages?.find(
        (l) => l.id === (exercise?.languageId || currentLanguage?.id),
      ),
      exerciseTranslations: languages?.map((l) => {
        if (!exercise) {
          return {
            languageId: l.id,
            title: '',
            description: '',
          };
        }

        const translation = exercise.exerciseTranslations.find(
          (t) => t.languageId === l.id,
        );

        if (!translation) {
          return {
            languageId: l.id,
            exerciseId: exercise.id,
            title: '',
            description: '',
          };
        }

        return translation;
      }) as ExerciseTranslation[],
      options: (exercise?.id
        ? exercise?.options
        : exerciseOptions?.filter((o) => o.default)
      )?.map((o) => o.id),
      canCopy: exercise?.canCopy || false,
      exerciseLoadFormula:
        formulas?.find((f) => f.id === exercise?.exerciseLoadFormulaId) || null,
      exerciseLoadFormulaId: exercise?.exerciseLoadFormulaId || null,
    },
    onSubmit,
  });

  useEffect(() => {
    if (exerciseId) getExercise(exerciseId);
  }, [exerciseId]);

  useEffect(() => {
    if (isExerciseLoading) {
      if (!exerciseLoaderIdRef.current) {
        exerciseLoaderIdRef.current = showLoader();
      }
      return;
    }

    if (exerciseLoaderIdRef.current) {
      hideLoader(exerciseLoaderIdRef.current);
      exerciseLoaderIdRef.current = null;
    }
  }, [hideLoader, isExerciseLoading, showLoader]);

  useEffect(() => {
    return () => {
      if (exerciseLoaderIdRef.current) {
        hideLoader(exerciseLoaderIdRef.current);
        exerciseLoaderIdRef.current = null;
      }
    };
  }, [hideLoader]);

  function isExerciseAvailable() {
    return (
      (error && 'status' in error && error?.status === 404) ||
      !!exercise?.ownerId
    );
  }

  useNotFound(isExerciseAvailable());

  async function onSubmit(update) {
    if (!isValidAvatar()) {
      formik.setFieldError(
        'avatarFileId',
        t('formikValidation.exerciseAvatarRequired'),
      );
      return;
    }

    if (!filePaths) {
      return;
    }

    const filteredTranslations = update.exerciseTranslations.filter(
      (translation) => translation.title?.trim(),
    );

    const loaderId = showLoader();
    const uploadedStoragePaths: string[] = [];
    let hasError = false;

    try {
      const uploadedFiles: IAppFile[] = await Promise.all(
        filesToUpload.map(async (file) => {
          const uploadedFile = await uploadFileAndCreateAppFile(
            file,
            filePaths.exercises,
          );

          uploadedStoragePaths.push(uploadedFile.storageLink);
          if (uploadedFile.previewStorageLink) {
            uploadedStoragePaths.push(uploadedFile.previewStorageLink);
          }
          return uploadedFile;
        }),
      );

      let mappedAvatarFileId = update.avatarFileId;
      if (typeof update.avatarFileId === 'number') {
        const fileIndex = filesToUpload.findIndex(
          (f) => f.id === update.avatarFileId,
        );
        if (fileIndex >= 0 && uploadedFiles[fileIndex]) {
          mappedAvatarFileId =
            uploadedFiles[fileIndex].id || update.avatarFileId;
        }
      }

      try {
        const response = await postPutPostExercise({
          id: exerciseId || undefined,
          body: {
            exercise: {
              // title: update.title,
              scope: update.scope?.id,
              // description: update.description,
              categoryId: update.category?.id || null,
              mainMuscleGroupId: update.mainMuscleGroup?.id || null,
              equipmentId: update.equipment?.id || null,
              additionalEquipmentIds:
                update.additionalEquipments?.map((eq) => eq.id) || [],
              additionalMuscleIds:
                update.additionalMuscles?.map((muscle) => muscle.id) || [],
              developedSkillIds: update.developedSkills?.map((s) => s.id) || [],
              languageId: update.language.id,
              avatarFileId: mappedAvatarFileId,
              canCopy: update.canCopy,
              splitBySide: update.splitBySide,
              options: update.options.map((id) => ({ id })),
              exerciseTranslations: filteredTranslations,
              exerciseLoadFormulaId: update.exerciseLoadFormulaId || null,
            },
            filesToUpload: uploadedFiles as any,
            fileIdsToDelete,
          },
        })
          .unwrap()
          .then(() =>
            options?.onSuccess ? options.onSuccess() : navigate(-1),
          );
      } catch (backendError) {
        hasError = true;
        console.error('Error saving exercise:', backendError);
        throw backendError;
      }
    } catch (error) {
      hasError = true;
      console.error('Error uploading files:', error);
      throw error;
    } finally {
      if (hasError && uploadedStoragePaths.length > 0) {
        await deleteFilesFromFirebase(uploadedStoragePaths);
      }
      hideLoader(loaderId);
    }
  }

  function isValidAvatar() {
    const remainingFiles =
      exercise?.files?.filter((file) => !fileIdsToDelete.includes(file.id)) ??
      [];

    const hasFiles = filesToUpload.length > 0 || remainingFiles.length > 0;

    const isAvatarValid =
      filesToUpload.some(
        (file) => file.id === (formik.values.avatarFileId as any),
      ) ||
      remainingFiles.some((file) => file.id === formik.values.avatarFileId);

    const avatarFile =
      filesToUpload.find(
        (file) => String(file.id) === String(formik.values.avatarFileId),
      ) ||
      remainingFiles.find(
        (file) => String(file.id) === String(formik.values.avatarFileId),
      );
    const isAvatarGif = avatarFile ? isGifMimeType(avatarFile.mimeType) : false;

    return !hasFiles || (isAvatarValid && !isAvatarGif);
  }

  const availableFormulas = useMemo(() => {
    if (
      !formulas ||
      !formik.values.options ||
      formik.values.options.length === 0
    ) {
      return [];
    }

    const selectedOptionIds = formik.values.options;
    const selectedOptions =
      exerciseOptions?.filter((opt) => selectedOptionIds.includes(opt.id)) ||
      [];

    const selectedNameKeys = selectedOptions.map((opt) => opt.nameKey);

    return formulas.filter(
      (formula) => selectedNameKeys.some((key) => formula.value.includes(key)), //todo improve this
    );
  }, [formulas, formik.values.options, exerciseOptions]);

  function onChangeFormikOption(e: ChangeEvent<HTMLInputElement>, option) {
    const previousOptions = formik.values.options;
    if (e.target.checked) {
      formik.setFieldValue('options', [...previousOptions, option.id]);
    } else {
      formik.setFieldValue(
        'options',
        previousOptions.filter((o) => o !== option.id),
      );
    }
    formik.setFieldValue('exerciseLoadFormula', null);
    formik.setFieldValue('exerciseLoadFormulaId', null);
  }

  function getResultFiles(): Array<IAppFile | INewFile> {
    return [
      ...(exercise?.files.filter((f) => !fileIdsToDelete.includes(f.id)) || []),
      ...filesToUpload,
    ];
  }

  function onFilesToDeleteChanged(ids: string[]) {
    setFileIdsToDelete(ids);
    formik.setFieldError('avatarFileId', null);

    const avatarFileId =
      formik.values.avatarFileId ??
      exercise?.avatarFileId ??
      exercise?.avatar?.id;
    if (avatarFileId != null && ids.includes(String(avatarFileId))) {
      formik.setFieldValue('avatarFileId', null);
    }
  }

  function onFilesToUploadChanged(files: INewFile[]) {
    formik.setFieldError('avatarFileId', null);

    const avatarFileId =
      formik.values.avatarFileId ??
      exercise?.avatarFileId ??
      exercise?.avatar?.id;
    const remainingFiles =
      exercise?.files?.filter(
        (f) => !fileIdsToDelete.some((fid) => fid === String(f.id)),
      ) ?? [];
    const hasValidAvatar =
      avatarFileId != null &&
      (remainingFiles.some((f) => String(f.id) === String(avatarFileId)) ||
        files?.some((f) => String(f.id) === String(avatarFileId)));

    const selectedAvatarFile =
      remainingFiles.find((f) => String(f.id) === String(avatarFileId)) ||
      files.find((f) => String(f.id) === String(avatarFileId));
    const isSelectedAvatarGif = selectedAvatarFile
      ? isGifMimeType(selectedAvatarFile.mimeType)
      : false;
    if (!hasValidAvatar || isSelectedAvatarGif) {
      const firstNonGifFile = [...remainingFiles, ...files].find(
        (file) => !isGifMimeType(file.mimeType),
      );
      formik.setFieldValue('avatarFileId', firstNonGifFile?.id ?? null);
    }

    setFilesToUpload(files);
  }

  function onMainFileChanged(id: string | number) {
    const selectedFile = getResultFiles().find(
      (file) => String(file.id) === String(id),
    );
    if (selectedFile && isGifMimeType(selectedFile.mimeType)) {
      formik.setFieldError(
        'avatarFileId',
        t('formikValidation.exerciseAvatarRequired'),
      );
      return;
    }
    formik.setFieldValue('avatarFileId', id);
    formik.setFieldError('avatarFileId', null);
  }

  async function onGenerateDescription(translationIndex: number) {
    const translation = formik.values.exerciseTranslations?.[translationIndex];
    const title = translation?.title?.trim();
    if (!title) {
      return;
    }
    try {
      const description = await generateDescription({ name: title }).unwrap();
      formik.setFieldValue(
        `exerciseTranslations[${translationIndex}].description`,
        description,
      );
    } catch (error) {
      console.error('Error generating description', error);
      // Error handled by TransformedErrorResponseWithNotification
    }
  }

  async function onTranslateMissing() {
    const translations = formik.values.exerciseTranslations || [];
    const sourceLanguageId = formik.values.language?.id;
    const sortedTranslations = [...translations].sort((a, b) => {
      if (a.languageId === sourceLanguageId) return -1;
      if (b.languageId === sourceLanguageId) return 1;
      return 0;
    });

    const items: TranslateItemRequest[] = sortedTranslations.map((t) => {
      const lang = languages.find((l) => l.id === t.languageId);
      const langKey = lang?.key || lang?.id || '';
      const title = t.title?.trim() || '';
      const description = t.description?.trim() || '';
      return {
        language: langKey,
        data: {
          name: title,
          description,
        },
      };
    });

    const hasAnyTranslation = items.some(
      (i) => !!(i.data?.name?.trim() || i.data?.description?.trim()),
    );
    const hasAnyMissing = items.some(
      (i) => !i.data || !i.data.name?.trim() || !i.data.description?.trim(),
    );
    if (!hasAnyTranslation || !hasAnyMissing) {
      return;
    }

    try {
      const result = await translateMissing({ items }).unwrap();
      const newTranslations = [...translations];
      const normalizeLangKey = (key: string) =>
        String(key || '')
          .toLowerCase()
          .trim();

      result.forEach((item) => {
        if (!item?.data) return;
        const itemLangKey = normalizeLangKey(item.language);
        const idx = newTranslations.findIndex((translation) => {
          const lang = languages.find((l) => l.id === translation.languageId);
          const langKey = normalizeLangKey(lang?.key || lang?.id || '');
          return langKey === itemLangKey;
        });
        if (idx >= 0) {
          const translatedName = item.data.name ?? newTranslations[idx].title;
          const translatedDescription =
            item.data.description ?? newTranslations[idx].description;
          newTranslations[idx] = {
            ...newTranslations[idx],
            title: translatedName,
            description: translatedDescription,
          };
        }
      });
      formik.setFieldValue('exerciseTranslations', newTranslations);
    } catch {
      // Error handled by TransformedErrorResponseWithNotification
    }
  }

  return {
    formik,
    exercise,
    fileIdsToDelete,
    filesToUpload,
    exerciseFiles: getResultFiles(),
    selectedFile,
    setSelectedFile,
    onFilesToDeleteChanged,
    onFilesToUploadChanged,
    onMainFileChanged,
    onChangeFormikOption,
    exerciseOptions,
    isLoading,
    translations,
    currentLanguage,
    languages: orderBy(languages, 'title'),
    categories,
    mainMuscleGroups,
    equipments,
    developedSkills,
    exerciseScope,
    availableFormulas,
    onGenerateDescription,
    onTranslateMissing,
    isGenerateDescriptionLoading,
    isTranslateMissingLoading,
    canUseDescriptionGenerate: aiUse?.canUseDescriptionGenerate ?? false,
    canUseTranslate: aiUse?.canUseTranslate ?? false,
    canUseAssistant: aiUse?.canUseAssistant ?? false,
  };
}
